import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";

const MAX_FREEZES = 3;
const today = () => new Date().toISOString().slice(0, 10);

export function useHabitOS(userId) {
  const [routineId, setRoutineIdState]  = useState("neuro");
  const [doneHabits, setDoneHabits]     = useState([]);
  const [streak, setStreak]             = useState(0);
  const [freezes, setFreezesState]      = useState(0);
  const [earnedFreezeToday, setEarned]  = useState(false);
  const [history, setHistory]           = useState({});
  const [customHabits, setCustomHabits] = useState({ morning: [], night: [] });
  const [subscription, setSubscription] = useState("free");
  const [loading, setLoading]           = useState(true);

  const [routineEdits, setRoutineEdits] = useState({});  // { routineId: { morning: [], night: [] } }

  useEffect(() => {
    if (!userId) return;
    loadAll();
  }, [userId]);

  async function loadAll() {
    setLoading(true);
    await Promise.all([
      loadRoutineState(),
      loadTodayHistory(),
      loadFullHistory(),
      loadCustomHabits(),
      loadSubscription(),
      loadRoutineEdits(),
    ]);
    setLoading(false);
  }

  async function loadRoutineEdits() {
    const { data } = await supabase
      .from("routine_edits")
      .select("*")
      .eq("user_id", userId);
    if (data) {
      const map = {};
      data.forEach(row => {
        map[row.routine_id] = {
          morning: row.morning_habits || [],
          night:   row.night_habits   || [],
        };
      });
      setRoutineEdits(map);
    }
  }

  async function loadRoutineState() {
    const { data } = await supabase
      .from("user_routines")
      .select("*")
      .eq("user_id", userId)
      .single();

    if (data) {
      setRoutineIdState(data.routine_id);
      setStreak(data.streak);
      setFreezesState(data.freezes);
      setEarned(data.earned_freeze_today);
    } else {
      // Erster Login: Datensatz anlegen
      await supabase
        .from("user_routines")
        .insert({ user_id: userId });
    }
  }

  async function loadTodayHistory() {
    const { data } = await supabase
      .from("habit_history")
      .select("done_habits")
      .eq("user_id", userId)
      .eq("date", today())
      .single();

    if (data) setDoneHabits(data.done_habits || []);
  }

  async function loadFullHistory() {
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

    const { data } = await supabase
      .from("habit_history")
      .select("date, status, done_habits, progress")
      .eq("user_id", userId)
      .gte("date", ninetyDaysAgo.toISOString().slice(0, 10))
      .order("date", { ascending: true });

    if (data) {
      const map = {};
      data.forEach((row) => { map[row.date] = row; });
      setHistory(map);
    }
  }

  async function loadCustomHabits() {
    const { data } = await supabase
      .from("custom_habits")
      .select("*")
      .eq("user_id", userId)
      .order("position", { ascending: true });

    if (data) {
      setCustomHabits({
        morning: data
          .filter((h) => h.section === "morning")
          .map((h) => ({ id: h.id, label: h.label, isHero: false })),
        night: data
          .filter((h) => h.section === "night")
          .map((h) => ({ id: h.id, label: h.label, isHero: false })),
      });
    }
  }

  async function loadSubscription() {
    const { data } = await supabase
      .from("subscriptions")
      .select("plan")
      .eq("user_id", userId)
      .single();

    if (data) setSubscription(data.plan);
  }

  // ── Habit abhaken ────────────────────────────────────────
  const toggleHabit = useCallback(async (habitId, allHabits, morningHabits) => {
    const newDone = doneHabits.includes(habitId)
      ? doneHabits.filter((id) => id !== habitId)
      : [...doneHabits, habitId];

    setDoneHabits(newDone);

    const progress     = Math.round((newDone.length / allHabits.length) * 100);
    const isComplete   = progress === 100;
    const allMorningDone = morningHabits.every((h) => newDone.includes(h.id));

    // History-Eintrag upserten (anlegen oder aktualisieren)
    await supabase
      .from("habit_history")
      .upsert({
        user_id:     userId,
        date:        today(),
        status:      isComplete ? "complete" : "incomplete",
        done_habits: newDone,
        progress,
      }, { onConflict: "user_id,date" });

    // Streak aktualisieren wenn Tag abgeschlossen
    if (isComplete) {
      const newStreak = streak + 1;
      setStreak(newStreak);
      await supabase
        .from("user_routines")
        .update({ streak: newStreak })
        .eq("user_id", userId);
    }

    // Freeze verdienen wenn alle Morning-Habits erledigt
    if (allMorningDone && !earnedFreezeToday && freezes < MAX_FREEZES) {
      const newFreezes = Math.min(MAX_FREEZES, freezes + 1);
      setFreezesState(newFreezes);
      setEarned(true);
      await supabase
        .from("user_routines")
        .update({ freezes: newFreezes, earned_freeze_today: true })
        .eq("user_id", userId);
    }

    return { isComplete, progress };
  }, [doneHabits, freezes, earnedFreezeToday, streak, userId]);

  // ── Protokoll wechseln ───────────────────────────────────
  const switchRoutine = useCallback(async (newId) => {
    setRoutineIdState(newId);
    setDoneHabits([]);
    setCustomHabits({ morning: [], night: [] });

    await supabase
      .from("user_routines")
      .update({ routine_id: newId, earned_freeze_today: false })
      .eq("user_id", userId);

    await supabase
      .from("habit_history")
      .upsert({
        user_id:     userId,
        date:        today(),
        status:      "incomplete",
        done_habits: [],
        progress:    0,
      }, { onConflict: "user_id,date" });
  }, [userId]);

  // ── Custom Habit hinzufügen ──────────────────────────────
  const addCustomHabit = useCallback(async (section, label) => {
    const { data } = await supabase
      .from("custom_habits")
      .insert({
        user_id:  userId,
        section,
        label,
        position: customHabits[section].length,
      })
      .select()
      .single();

    if (data) {
      setCustomHabits((prev) => ({
        ...prev,
        [section]: [
          ...prev[section],
          { id: data.id, label: data.label, isHero: false },
        ],
      }));
    }
  }, [userId, customHabits]);

  // ── Custom Habit löschen ─────────────────────────────────
  const removeCustomHabit = useCallback(async (habitId, section) => {
    await supabase
      .from("custom_habits")
      .delete()
      .eq("id", habitId);

    setCustomHabits((prev) => ({
      ...prev,
      [section]: prev[section].filter((h) => h.id !== habitId),
    }));
  }, []);

  // ── Routine-Bearbeitung speichern ───────────────────────
  const saveRoutineEdit = useCallback(async (rId, morning, night) => {
    const payload = {
      user_id:        userId,
      routine_id:     rId,
      morning_habits: morning,
      night_habits:   night,
      updated_at:     new Date().toISOString(),
    };

    await supabase
      .from("routine_edits")
      .upsert(payload, { onConflict: "user_id,routine_id" });

    setRoutineEdits(prev => ({
      ...prev,
      [rId]: { morning, night },
    }));
  }, [userId]);

  // ── Routine-Bearbeitung zurücksetzen ─────────────────────
  const resetRoutineEdit = useCallback(async (rId) => {
    await supabase
      .from("routine_edits")
      .delete()
      .eq("user_id", userId)
      .eq("routine_id", rId);

    setRoutineEdits(prev => {
      const next = { ...prev };
      delete next[rId];
      return next;
    });
  }, [userId]);

  return {
    routineId,
    doneHabits,
    streak,
    freezes,
    earnedFreezeToday,
    history,
    customHabits,
    routineEdits,
    subscription,
    loading,
    toggleHabit,
    switchRoutine,
    addCustomHabit,
    removeCustomHabit,
    saveRoutineEdit,
    resetRoutineEdit,
  };
}